
import { writeArrow, writeTrail } from "./effects";
import { INSTANCE_FLOATS } from "./particles";

export const MM_PER_LEGACY_UNIT = 10;

export interface Point {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

/** A script's `TailFlag 1` block, the streak `CFlyTrace::Create` is given
 *  (`FlyingData.cpp:252-261`, `FlyTrace.cpp:71-78`). */
export interface FlyTail {
  /** `TailColor`, a D3DCOLOR, as unit RGBA. */
  readonly colour: readonly [number, number, number, number];
  /** `TailLength`: how many seconds of samples the tail keeps. */
  readonly lengthS: number;
  /** `TailSize`, in mm: the half width at the head. */
  readonly halfWidthMm: number;
  /** `TailShapeRect`: keep the full width to the end instead of tapering. */
  readonly rect: boolean;
}

/** A D3DCOLOR `0xAARRGGBB` as unit RGBA, the vertex diffuse `FlyTrace::Render`
 *  draws with (`FlyTrace.cpp:173-178`). */
export function tailColour(argb: number): [number, number, number, number] {
  return [((argb >>> 16) & 0xff) / 255, ((argb >>> 8) & 0xff) / 255, (argb & 0xff) / 255, (argb >>> 24) / 255];
}

const tailOf = (argb: number, lengthS: number, size: number, rect: number): FlyTail => ({
  colour: tailColour(argb),
  lengthS,
  halfWidthMm: unit(size),
  rect: rect !== 0,
});

export interface FlyScript {
  /** The `.msf` file, as the `.msa`'s `FlyFileName` spells it. */
  readonly file: string;
  /** `InitialVelocity`, in mm/s. */
  readonly speedMmS: number;
  readonly accelMmS2: number;
  readonly gravityMmS2: number;
  readonly rangeMm: number;
  /** `HomingFlag`: does it re-aim at the target's live position each step. */
  readonly homing: boolean;
  /** `HomingStartTime`, seconds after launch before homing begins. Zero in all
   *  fourteen (`FlyingInstance.cpp:352-353`). */
  readonly homingStartS: number;
  readonly homingMaxAngleDeg: number;
  /** `BombRange`, in mm: how close the segment swept this step must come to the
   *  target's centre to count as arrived (`FlyingInstance.cpp:381-385`). */
  readonly bombRangeMm: number;
  /** `BombEffect`: what plays where it lands. A `.mse` name for the caller to
   *  hand to `EffectRenderer.spawn`; nothing is played for an expiry, exactly as
   *  `__Explode(false)` skips the bomb on an out-of-range death
   *  (`FlyingInstance.cpp:373`). */
  readonly bombEffect: string;
  readonly attachEffect: string;
  /** `PierceCount`: how many arrivals it survives before dying
   *  (`FlyingInstance.cpp:401-405`). Zero everywhere but Dragon Swirl. */
  readonly pierceCount: number;
  /** The first `AttachData`'s tail, or null for `TailFlag 0`. */
  readonly tail: FlyTail | null;
  /** `MaintainParallelFlag`: aim fifty units above the target, so a bolt
   *  loosed from the chest flies level (`FlyingData.cpp:147-154`,
   *  `FlyingInstance.cpp:110-122`). Absent is false. */
  readonly maintainParallel?: boolean;
  /** `SpreadingFlag`, `ConeAngle` and `RollAngle`: how the local forward is
   *  turned before it is aimed. */
  readonly cone: FlyCone;
  /** `AngularVelocity`, degrees per second about the local x, y and z: the
   *  attached effect's spin (`FlyingInstance.cpp:195-200`). */
  readonly spinDegS: readonly [number, number, number];
  /** The `AttachData`'s placement of the effect about the flight's head. */
  readonly attach: FlyAttach;
}

/** A script's launch turn (`FlyingData.cpp:138-145,161-167`): the local
 *  forward is rolled `ConeAngle` about z and yawed `RollAngle - 90` about y,
 *  and a spreading script first tilts it a random cone about its own axis
 *  (`FlyingInstance.cpp:578-586`). */
export interface FlyCone {
  readonly spreading: boolean;
  readonly coneDeg: number;
  readonly rollDeg: number;
}

/** `FLY_ATTACH_TYPE_*` (`FlyingData.h:14-21`). */
export const FLY_ATTACH_TYPE_LINE = 1; // FlyingData.h:17
export const FLY_ATTACH_TYPE_MULTI_LINE = 2; // FlyingData.h:18
export const FLY_ATTACH_TYPE_SINE = 3; // FlyingData.h:19
export const FLY_ATTACH_TYPE_EXP = 4; // FlyingData.h:20

/** One `Group AttachData`'s placement (`FlyingData.cpp:240,267-270`):
 *  `FlyType` picks the rule, `Roll` the side of the axis it pushes to,
 *  `Distance` the fixed push of `MULTI_LINE`, and `Period` with `Amplitude`
 *  the weave of `SINE` and `EXP`. */
export interface FlyAttach {
  readonly flyType: number;
  readonly rollDeg: number;
  readonly distanceMm: number;
  readonly periodS: number;
  readonly amplitudeMm: number;
}

const unit = (legacy: number): number => legacy * MM_PER_LEGACY_UNIT;

const coneOf = (spreading: number, coneDeg: number, rollDeg: number): FlyCone => ({
  spreading: spreading !== 0,
  coneDeg,
  rollDeg,
});

const attachOf = (flyType: number, rollDeg: number, distance: number, periodS: number, amplitude: number): FlyAttach => ({
  flyType,
  rollDeg,
  distanceMm: unit(distance),
  periodS,
  amplitudeMm: unit(amplitude),
});

export const FLY_SCRIPTS: Readonly<Record<string, FlyScript>> = {
  // `pc/warrior/effect/geompung.msf`
  geompung: {
    file: "pc/warrior/effect/geompung.msf",
    speedMmS: unit(2000),
    accelMmS2: unit(3000),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 3,
    bombRangeMm: unit(10),
    bombEffect: "geompung_blow.mse",
    attachEffect: "geompung.mse",
    pierceCount: 0,
    tail: tailOf(3427639039, 0.15, 7, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/assassin/effect/beam.msf`
  beam: {
    file: "pc/assassin/effect/beam.msf",
    speedMmS: unit(2000),
    accelMmS2: unit(9000),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 3,
    bombRangeMm: unit(10),
    bombEffect: "beam_blow.mse",
    attachEffect: "beam.mse",
    pierceCount: 0,
    tail: tailOf(3427257959, 0.15, 7, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/assassin/effect/gwangyeok.msf`
  gwangyeok: {
    file: "pc/assassin/effect/gwangyeok.msf",
    speedMmS: unit(4000),
    accelMmS2: 0,
    gravityMmS2: 0,
    rangeMm: unit(5000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "d:/ymir work/effect/hit/blow_1/blow_1_low_center.mse",
    attachEffect: "gwangyeoksul.mse",
    pierceCount: 0,
    tail: tailOf(3435973836, 0.18, 1, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/assassin/effect/hwajo.msf`
  hwajo: {
    file: "pc/assassin/effect/hwajo.msf",
    speedMmS: unit(3000),
    accelMmS2: unit(9000),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 3,
    bombRangeMm: unit(10),
    bombEffect: "hwajopa_blow.mse",
    attachEffect: "hwajopa.mse",
    pierceCount: 0,
    tail: tailOf(3438134194, 0.1, 6, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/sura/effect/pabeop_throw.msf`
  pabeop_throw: {
    file: "pc/sura/effect/pabeop_throw.msf",
    speedMmS: unit(1000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "pabeopsul_blow.mse",
    attachEffect: "pabeopsul_throw.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/sura/effect/maryeong_throw.msf`
  maryeong_throw: {
    file: "pc/sura/effect/maryeong_throw.msf",
    speedMmS: unit(3000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "maryeong_blow.mse",
    attachEffect: "maryeong_throw.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/sura/effect/tusok_throw.msf`
  tusok_throw: {
    file: "pc/sura/effect/tusok_throw.msf",
    speedMmS: unit(3000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "tusok_blow.mse",
    attachEffect: "tusok_throw.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/sura/effect/geomhwan_throw.msf` -- Dark Orb's, despite the name. See
  // the vnum 81 entry below.
  geomhwan_throw: {
    file: "pc/sura/effect/geomhwan_throw.msf",
    speedMmS: unit(3000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(50),
    bombEffect: "geomhwangyeok_blow.mse",
    attachEffect: "geomhwangyeok.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // `pc/shaman/effect/bipabu.msf`
  bipabu: {
    file: "pc/shaman/effect/bipabu.msf",
    speedMmS: unit(2000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(5000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "1bipa_blow.mse",
    attachEffect: "1bipa_throw.mse",
    pierceCount: 0,
    tail: tailOf(524032, 0.2, 3, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 1, 0],
    attach: attachOf(2, 270, 5, 0.1, 100),
  },
  // `pc/shaman/effect/yongpa.msf` -- the one that does not home and the one that
  // pierces: Dragon Swirl is a line the caster fires through a crowd, not a
  // seeker, and `PierceCount 10` is what lets it keep going through ten of them.
  yongpa: {
    file: "pc/shaman/effect/yongpa.msf",
    speedMmS: unit(1800),
    accelMmS2: 0,
    gravityMmS2: 0,
    rangeMm: unit(1250),
    homing: false,
    homingStartS: 0,
    homingMaxAngleDeg: 3,
    bombRangeMm: unit(10),
    bombEffect: "pokroe_c.mse",
    attachEffect: "yongpa.mse",
    pierceCount: 10,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 100, 0, 1, 100),
    maintainParallel: true,
  },
  // `pc/shaman/effect/noejeon_throw.msf`
  noejeon_throw: {
    file: "pc/shaman/effect/noejeon_throw.msf",
    speedMmS: unit(2000),
    accelMmS2: unit(100),
    gravityMmS2: 0,
    rangeMm: unit(5000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "noejeon_blow.mse",
    attachEffect: "noejeon_throw.mse",
    pierceCount: 0,
    tail: tailOf(524032, 0.2, 3, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 1, 0],
    attach: attachOf(2, 270, 5, 0.1, 100),
  },
  // `pc/shaman/effect/pokroe.msf` -- the second non-homer.
  pokroe: {
    file: "pc/shaman/effect/pokroe.msf",
    speedMmS: unit(3000),
    accelMmS2: 0,
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: false,
    homingStartS: 0,
    homingMaxAngleDeg: 3,
    bombRangeMm: unit(10),
    bombEffect: "pokroe_c.mse",
    attachEffect: "pokroe_b.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(1, 0, 0, 1, 0),
  },
  // The seven `etc/gathering/*.msf` motes `RegisterIndexedFlyData` registers
  // (`playersettingmodule.py:419-424,433`). All seven share one shape: they are
  // thrown *backwards* out of the source at 100 units/s and then hauled in by a
  // 500 unit/s^2 pull along the same axis, which is why `InitialVelocity` is
  // negative and the acceleration is not -- the mote pops off the corpse before
  // it swoops. Homing opens a second after launch (`HomingStartTime 1`), so the
  // pop happens before the mote can turn.
  // `effect/etc/gathering/ga_piece_yellow_small2.msf`
  ga_exp: {
    file: "effect/etc/gathering/ga_piece_yellow_small2.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_yellow.mse",
    attachEffect: "ga_piece_yellow2.mse",
    pierceCount: 0,
    tail: tailOf(1207629424, 0.3, 2, 0),
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(2, 0, 20, 0.4, 150),
  },
  // `effect/etc/gathering/ga_piece_red_small.msf`
  ga_hp_medium: {
    file: "effect/etc/gathering/ga_piece_red_small.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: unit(3),
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_red.mse",
    attachEffect: "ga_piece_red.mse",
    pierceCount: 0,
    tail: tailOf(771730347, 0.3, 3, 0),
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(4, 90, 10, 1, 100),
  },
  // `effect/etc/gathering/ga_piece_red_big.msf`
  ga_hp_big: {
    file: "effect/etc/gathering/ga_piece_red_big.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center.mse",
    attachEffect: "ga_spirit_red.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(3, 0, 0, 1, 0),
  },
  // `effect/etc/gathering/ga_piece_red_smallest.msf`
  ga_hp_small: {
    file: "effect/etc/gathering/ga_piece_red_smallest.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_blue_warrior.mse",
    attachEffect: "ga_center_small_blue_warrior.mse",
    pierceCount: 0,
    tail: tailOf(4294901760, 0.2, 1, 0),
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(4, 0, 0, 0.4, 150),
  },
  // `effect/etc/gathering/ga_piece_blue_warrior_small.msf`
  ga_sp_small: {
    file: "effect/etc/gathering/ga_piece_blue_warrior_small.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_blue_warrior.mse",
    attachEffect: "ga_center_small_blue_warrior.mse",
    pierceCount: 0,
    tail: tailOf(4278190335, 0.2, 1, 0),
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(4, 0, 0, 0.4, 150),
  },
  // `effect/etc/gathering/ga_piece_blue_small.msf`
  ga_sp_medium: {
    file: "effect/etc/gathering/ga_piece_blue_small.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_blue.mse",
    attachEffect: "ga_piece_blue.mse",
    pierceCount: 0,
    tail: tailOf(754974975, 0.3, 3, 0),
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(4, 90, 10, 1, 100),
  },
  // `effect/etc/gathering/ga_piece_blue_big.msf`
  ga_sp_big: {
    file: "effect/etc/gathering/ga_piece_blue_big.msf",
    speedMmS: unit(-100),
    accelMmS2: unit(500),
    gravityMmS2: 0,
    rangeMm: unit(10000),
    homing: true,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "ga_center_small_blue.mse",
    attachEffect: "ga_spirit_blue.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(1, 90, 0),
    spinDegS: [0, 450, 0],
    attach: attachOf(3, 0, 0, 1, 0),
  },
  // The seven fireworks (`playersettingmodule.py:425-431`). One flight, seven
  // bursts: every `etc/firecracker/*.msf` carries the same numbers and the same
  // `firecracker_fly.mse` mote, and differs only in which shell it opens into.
  // `effect/etc/firecracker/firecracker_1.msf`
  firecracker_1: {
    file: "effect/etc/firecracker/firecracker_1.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_1.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1972240639, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 300, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_2.msf`
  firecracker_2: {
    file: "effect/etc/firecracker/firecracker_2.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_2.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1979708672, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 300, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_3.msf`
  firecracker_3: {
    file: "effect/etc/firecracker/firecracker_3.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_3.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1971650303, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 300, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_4.msf`
  firecracker_4: {
    file: "effect/etc/firecracker/firecracker_4.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_4.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(2365521920, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 3000, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_5.msf`
  firecracker_5: {
    file: "effect/etc/firecracker/firecracker_5.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_5.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1962999586, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 3000, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_6.msf`
  firecracker_6: {
    file: "effect/etc/firecracker/firecracker_6.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_6.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1979711487, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 3000, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `effect/etc/firecracker/firecracker_xmas.msf`
  firecracker_xmas: {
    file: "effect/etc/firecracker/firecracker_xmas.msf",
    speedMmS: unit(500),
    accelMmS2: unit(800),
    gravityMmS2: 0,
    rangeMm: unit(100000),
    homing: false,
    homingStartS: 1,
    homingMaxAngleDeg: 5,
    bombRangeMm: unit(40),
    bombEffect: "firecracker_xmas.mse",
    attachEffect: "firecracker_fly.mse",
    pierceCount: 0,
    tail: tailOf(1979708672, 1, 2, 0),
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 300, 0],
    attach: attachOf(2, 0, 10, 0.4, 150),
  },
  // `pc/sura/effect/muyeong_fly.msf` (`playersettingmodule.py:434`). The one
  // fly with a 180-degree turn limit: Sword Wind's blades reach their target
  // whatever it does.
  muyeong_fly: {
    file: "pc/sura/effect/muyeong_fly.msf",
    speedMmS: unit(1000),
    accelMmS2: 0,
    gravityMmS2: 0,
    rangeMm: unit(1000),
    homing: true,
    homingStartS: 0,
    homingMaxAngleDeg: 180,
    bombRangeMm: unit(10),
    bombEffect: "muyeong_blow.mse",
    attachEffect: "muyeongfly.mse",
    pierceCount: 0,
    tail: null,
    cone: coneOf(0, 0, 0),
    spinDegS: [0, 0, 0],
    attach: attachOf(3, 30, 0, 0.4, 30),
  },
};

/** What a flight is flying by. A skill's FLY event names one (`SKILL_FLIES`);
 *  so does a `CreateFly` type byte (`INDEXED_FLIES`). */
export interface FlightKind {
  readonly script: FlyScript;
}

/** One skill's FLY event: the `.msa` half of the pair. */
export interface SkillFly extends FlightKind {
  /** Skill vnum, as `metin3/data/skills.json` numbers them. */
  readonly vnum: number;
  /** The motion `playersettingmodule.py` registers for this vnum. */
  readonly motion: string;
  /** Which `Event%02d` of that motion is the FLY one. */
  readonly event: string;
  readonly startingTimeS: number;
  /** `AttachingBoneName`: where on the caster it leaves from. */
  readonly bone: string;
  readonly flyOffsetMm: Point;
  /** The `.msf` this event names, by its key in `FLY_SCRIPTS`. */
  readonly script: FlyScript;
}

const fly = (
  vnum: number,
  motion: string,
  event: string,
  startingTimeS: number,
  bone: string,
  offsetY: number,
  script: keyof typeof FLY_SCRIPTS,
): [number, SkillFly] => [
  vnum,
  {
    vnum,
    motion,
    event,
    startingTimeS,
    bone,
    flyOffsetMm: { xMm: 0, yMm: unit(offsetY), zMm: 0 },
    script: FLY_SCRIPTS[script],
  },
];

export const SKILL_FLIES: ReadonlyMap<number, SkillFly> = new Map([
  // Warrior, mental group. `playersettingmodule.py:526`.
  fly(20, "warrior/skill/geompung.msa", "Event01", 0.677859, "Bip01 R Hand", 0, "geompung"),
  // Assassin, body group. `playersettingmodule.py:704`.
  fly(35, "assassin/skill/sangong.msa", "Event01", 0.638686, "Bip01 Head", 0, "beam"),
  // Assassin, mental group. `playersettingmodule.py:706-710`.
  fly(46, "assassin/skill/yeonsa.msa", "Event02", 1.377129, "equip_right", -100, "gwangyeok"),
  fly(47, "assassin/skill/gwangyeok.msa", "Event02", 1.45, "equip_right", -100, "gwangyeok"),
  fly(48, "assassin/skill/hwajo.msa", "Event02", 1.318735, "equip_right", -30, "hwajo"),
  fly(50, "assassin/skill/dokgigung.msa", "Event01", 1.372263, "equip_right", -30, "beam"),
  // Sura, body group. `playersettingmodule.py:918`.
  fly(66, "sura/skill/pabeop.msa", "Event01", 1.189744, "Bip01 L Hand", 0, "pabeop_throw"),
  // Sura, mental group. `playersettingmodule.py:919,923,924`.
  fly(76, "sura/skill/maryeong.msa", "Event01", 0.738462, "Bip01 L Hand", 0, "maryeong_throw"),
  fly(80, "sura/skill/tusok.msa", "Event01", 0.738462, "Bip01 L Hand", 0, "tusok_throw"),
  fly(81, "sura/skill/mahwan.msa", "Event02", 1.389744, "equip_right", 0, "geomhwan_throw"),
  // Shaman, dragon group. `playersettingmodule.py:1115-1116`.
  fly(91, "shaman/skill/bipabu.msa", "Event01", 1.09854, "Bip01 R Finger2", 0, "bipabu"),
  fly(92, "shaman/skill/yongpa.msa", "Event00", 1.069343, "equip_right", 0, "yongpa"),
  // Shaman, healing group. `playersettingmodule.py:1121,1123`.
  fly(106, "shaman/skill/noejeon.msa", "Event01", 1.076642, "Bip01 R Finger2", 0, "noejeon_throw"),
  fly(108, "shaman/skill/pokroe.msa", "Event01", 1.087591, "equip_right", 0, "pokroe"),
]);

export function skillFlyFor(vnum: number): SkillFly | null {
  return SKILL_FLIES.get(vnum) ?? null;
}

/** One fly the server names by index rather than by skill: the `.msf` half of
 *  `RegisterIndexedFlyData(FLY_*, INDEX_FLY_TYPE_*, "<file>")`. */
export interface IndexedFly extends FlightKind {
  /** The `FLY_*` index `TPacketGCCreateFly.bType` carries (`char.h:61-79`). */
  readonly type: number;
  /** The `INDEX_FLY_TYPE_*` it was registered under, which decides where
   *  `CreateIndexedFly` throws it (`FlyingObjectManager.cpp:179-212`). */
  readonly launch: "normal" | "fireCracker" | "autoFire";
}

const indexed = (
  type: number,
  script: keyof typeof FLY_SCRIPTS,
  launch: IndexedFly["launch"] = "normal",
): [number, IndexedFly] => [
  type,
  { type, launch, script: FLY_SCRIPTS[script] },
];

/** `playersettingmodule.py:419-434`, in the enum's own order (`char.h:61-79`).
 *  `FLY_NONE` is 0 and registers nothing, so the table starts at 1. */
export const INDEXED_FLIES: ReadonlyMap<number, IndexedFly> = new Map([
  indexed(1, "ga_exp"), // FLY_EXP
  indexed(2, "ga_hp_medium"), // FLY_HP_MEDIUM
  indexed(3, "ga_hp_big"), // FLY_HP_BIG
  indexed(4, "ga_sp_small"), // FLY_SP_SMALL
  indexed(5, "ga_sp_medium"), // FLY_SP_MEDIUM
  indexed(6, "ga_sp_big"), // FLY_SP_BIG
  indexed(7, "firecracker_1", "fireCracker"), // FLY_FIREWORK1
  indexed(8, "firecracker_2", "fireCracker"), // FLY_FIREWORK2
  indexed(9, "firecracker_3", "fireCracker"), // FLY_FIREWORK3
  indexed(10, "firecracker_4", "fireCracker"), // FLY_FIREWORK4
  indexed(11, "firecracker_5", "fireCracker"), // FLY_FIREWORK5
  indexed(12, "firecracker_6", "fireCracker"), // FLY_FIREWORK6
  indexed(13, "firecracker_xmas", "fireCracker"), // FLY_FIREWORK_CHRISTMAS
  indexed(14, "pokroe"), // FLY_CHAIN_LIGHTNING
  indexed(15, "ga_hp_small"), // FLY_HP_SMALL
  indexed(16, "muyeong_fly", "autoFire"), // FLY_SKILL_MUYEONG
]);

export function indexedFlyFor(type: number): IndexedFly | null {
  return INDEXED_FLIES.get(type) ?? null;
}

export const BONE_HEIGHT_MM: Readonly<Record<string, number>> = {
  "Bip01 R Hand": 1_110,
  "Bip01 L Hand": 1_110,
  "Bip01 R Finger2": 1_110,
  equip_right: 1_110,
  "Bip01 Head": 1_700,
};

/** Where `fly` puts its launch point, given where the caster is standing. */
export function launchPoint(kind: SkillFly, caster: Point): Point {
  return {
    xMm: caster.xMm + kind.flyOffsetMm.xMm,
    yMm: caster.yMm + kind.flyOffsetMm.yMm,
    zMm: caster.zMm + (BONE_HEIGHT_MM[kind.bone] ?? 1_110),
  };
}

export function flightMs(script: FlyScript, distanceMm: number): number {
  if (!(distanceMm > 0)) return 0;
  if (distanceMm > script.rangeMm) return Number.POSITIVE_INFINITY;
  const v = script.speedMmS;
  const a = script.accelMmS2;
  // d = v t + a t^2 / 2, solved for t.
  const t = a === 0 ? distanceMm / v : (Math.sqrt(v * v + 2 * a * distanceMm) - v) / a;
  return t * 1000;
}

/** One projectile in the air. Mutable, because it is integrated in place the way
 *  `CFlyingInstance::Update` integrates its own members. */
export interface Flight {
  readonly key: string;
  readonly vnum: number;
  readonly kind: FlightKind;
  readonly from: Point;
  /** Where it was aimed when it launched. A homing flight re-aims away from
   *  this; a non-homing one flies at it and no further. */
  readonly to: Point;
  readonly startMs: number;
  /** Head of the projectile, now. */
  at: Point;
  /** Where the head was at the end of the previous step: the other end of the
   *  segment the arrival test sweeps, and the tail `writeFlights` draws to. */
  previous: Point;
  /** Direction of travel, a unit vector. */
  direction: Point;
  /** Current scalar speed in mm/s: `InitialVelocity` plus whatever
   *  `Acceleration` has added since. Homing rotates the velocity and never
   *  scales it (`FlyingInstance.cpp:186`), so speed and direction are
   *  independent and one scalar is enough. */
  speedMmS: number;
  /** `m_fRemainRange`, in mm. */
  remainingMm: number;
  /** `m_iPierceCount`, decremented on each survived arrival. */
  piercesLeft: number;
  /** `m_qRot`, in legacy render space: the launch cone, then the aim, then
   *  every homing turn (`FlyingInstance.cpp:128-129,188,578-585`). */
  turn: Quaternion;
  /** `m_qAttachRotation`: the spin `AngularVelocity` has wound up since launch
   *  (`FlyingInstance.cpp:195-200`). */
  spin: Quaternion;
  /** Where the attached effect is now: the head, pushed off its axis by the
   *  script's `FlyType` (`FlyingInstance.cpp:211-274`). This is what is drawn
   *  and what the tail samples. */
  attachAt: Point;
  /** `attachAt` one step ago: the other end of the streak drawn. */
  attachPrevious: Point;
  /** `CFlyTrace::m_TimePositionDeque`: where the attached effect has been,
   *  newest first, no older than the script's `TailLength`. Empty for a script
   *  with no tail. */
  trail: TraceSample[];
}

/** One time-stamped point of a tail. */
export interface TraceSample extends Point {
  readonly atMs: number;
}

/** `CFlyTrace::UpdateNewPosition` (`FlyTrace.cpp:61-69`): push the head's
 *  point at the front and drop every sample from the back that is older than
 *  `lengthS`. */
export function traceSample(trail: TraceSample[], at: Point, nowMs: number, lengthS: number): void {
  trail.unshift({ xMm: at.xMm, yMm: at.yMm, zMm: at.zMm, atMs: nowMs });
  while (trail.length > 0 && trail[trail.length - 1]!.atMs + lengthS * 1000 < nowMs) trail.pop();
}

/** Why a flight ended. */
export type Ending = "arrived" | "out-of-range";

/** A flight that has finished, and where. */
export interface Landing {
  readonly flight: Flight;
  readonly ending: Ending;
  /** Where it ended. The impact effect goes here. */
  readonly at: Point;
  /** The `.mse` to play, or `null` for an expiry -- `__Explode(false)` plays no
   *  bomb when a flight runs out of range (`FlyingInstance.cpp:373`). */
  readonly bombEffect: string | null;
}

function subtract(a: Point, b: Point): Point {
  return { xMm: a.xMm - b.xMm, yMm: a.yMm - b.yMm, zMm: a.zMm - b.zMm };
}

function length(v: Point): number {
  return Math.sqrt(v.xMm * v.xMm + v.yMm * v.yMm + v.zMm * v.zMm);
}

function normalise(v: Point): Point | null {
  const n = length(v);
  if (!(n > 0)) return null;
  return { xMm: v.xMm / n, yMm: v.yMm / n, zMm: v.zMm / n };
}

function scaled(v: Point, k: number): Point {
  return { xMm: v.xMm * k, yMm: v.yMm * k, zMm: v.zMm * k };
}

function dot(a: Point, b: Point): number {
  return a.xMm * b.xMm + a.yMm * b.yMm + a.zMm * b.zMm;
}

export function segmentDistanceMm(a: Point, b: Point, p: Point): number {
  const ab = subtract(b, a);
  const ap = subtract(p, a);
  const denominator = dot(ab, ab);
  if (denominator === 0) return length(ap);
  const t = Math.max(0, Math.min(1, dot(ap, ab) / denominator));
  return length({
    xMm: ap.xMm - ab.xMm * t,
    yMm: ap.yMm - ab.yMm * t,
    zMm: ap.zMm - ab.zMm * t,
  });
}

/** A D3DX quaternion, `[x, y, z, w]`. */
export type Quaternion = readonly [number, number, number, number];

const IDENTITY: Quaternion = [0, 0, 0, 1];
/** The script's local forward: `m_v3LocalVelocity` is `(0, -InitialVelocity,
 *  0)` (`FlyingInstance.cpp:587`). */
const FORWARD: Point = { xMm: 0, yMm: -1, zMm: 0 };

const radians = (deg: number): number => (deg * Math.PI) / 180;

/** The wire's point in the original's render space, which negates y
 *  (`NetworkActorManager.cpp:373`); its own inverse. */
function rendered(v: Point): Point {
  return { xMm: v.xMm, yMm: -v.yMm, zMm: v.zMm };
}

/** `D3DXQuaternionMultiply(out, a, b)`: the rotation `a` followed by `b`. */
export function followedBy(a: Quaternion, b: Quaternion): Quaternion {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    bw * ax + aw * bx + (by * az - bz * ay),
    bw * ay + aw * by + (bz * ax - bx * az),
    bw * az + aw * bz + (bx * ay - by * ax),
    bw * aw - (bx * ax + by * ay + bz * az),
  ];
}

/** `D3DXQuaternionRotationAxis` about a unit axis. */
function rotationAxis(x: number, y: number, z: number, angle: number): Quaternion {
  const s = Math.sin(angle / 2);
  return [x * s, y * s, z * s, Math.cos(angle / 2)];
}

/** `D3DXQuaternionRotationYawPitchRoll`: roll about z, then pitch about x,
 *  then yaw about y. */
export function yawPitchRoll(yaw: number, pitch: number, roll: number): Quaternion {
  return followedBy(followedBy(rotationAxis(0, 0, 1, roll), rotationAxis(1, 0, 0, pitch)), rotationAxis(0, 1, 0, yaw));
}

/** `Vec3TransformQuaternion` (`GrpMath.h:110-118`). */
export function rotated(v: Point, q: Quaternion): Point {
  const [x, y, z, w] = q;
  const cx = v.yMm * z - v.zMm * y;
  const cy = v.zMm * x - v.xMm * z;
  const cz = v.xMm * y - v.yMm * x;
  const scale = w * w - (x * x + y * y + z * z);
  const along = 2 * (x * v.xMm + y * v.yMm + z * v.zMm);
  return {
    xMm: cx * -2 * w + scale * v.xMm + along * x,
    yMm: cy * -2 * w + scale * v.yMm + along * y,
    zMm: cz * -2 * w + scale * v.zMm + along * z,
  };
}

/** `SafeRotationNormalizedArc` (`GrpMath.h:47-59`): the shortest turn from one
 *  unit vector onto another. */
export function arcBetween(from: Point, to: Point): Quaternion {
  if (from.xMm === to.xMm && from.yMm === to.yMm && from.zMm === to.zMm) return IDENTITY;
  if (from.xMm === -to.xMm && from.yMm === -to.yMm && from.zMm === -to.zMm) return [0, 0, 1, 0]; // GrpMath.h:52
  const d = dot(from, to);
  const s = Math.sqrt((1 + d) * 2); // GrpMath.h:56
  return [
    (from.yMm * to.zMm - from.zMm * to.yMm) / s,
    (from.zMm * to.xMm - from.xMm * to.zMm) / s,
    (from.xMm * to.yMm - from.yMm * to.xMm) / s,
    s * 0.5, // GrpMath.h:58
  ];
}

/** `__SetDataPointer`'s turn and `__SetTargetNormalizedDirection`'s aim
 *  (`FlyingInstance.cpp:126-132,578-586`): the frame a flight launches in, in
 *  render space, for a unit `aim` in render space. `random` is `frandom`'s
 *  `[0, 1)` draw (`Random.cpp:38-41`). */
export function launchTurn(cone: FlyCone, aim: Point, random: () => number): Quaternion {
  let turn = yawPitchRoll(radians(cone.rollDeg - 90), 0, radians(cone.coneDeg)); // FlyingInstance.cpp:578
  if (cone.spreading) {
    const frandom = (low: number, high: number): number => random() * (high - low) + low; // Random.cpp:40
    const third = 3.141592 / 3; // FlyingInstance.cpp:582
    const tilt = rotationAxis(0, 0, 1, (frandom(-third, third) + frandom(-third, third)) / 2); // FlyingInstance.cpp:582
    const around = rotationAxis(0, -1, 0, frandom(0, 2 * 3.1415926535897931)); // FlyingInstance.cpp:583
    turn = followedBy(followedBy(tilt, around), turn); // FlyingInstance.cpp:584-585
  }
  return followedBy(turn, arcBetween(FORWARD, aim)); // FlyingInstance.cpp:128-129
}

/** How far `UpdateAttachInstance` pushes the attached effect off the head's
 *  axis, `seconds` after launch (`FlyingInstance.cpp:211-274`), before the
 *  push is turned by the roll. */
export function attachReachMm(attach: FlyAttach, seconds: number): number {
  switch (attach.flyType) {
    case FLY_ATTACH_TYPE_MULTI_LINE:
      return attach.distanceMm; // FlyingInstance.cpp:222-225
    case FLY_ATTACH_TYPE_SINE:
      return attach.amplitudeMm * Math.sin((seconds * 2 * 3.1415926535897931) / attach.periodS); // FlyingInstance.cpp:239-243
    case FLY_ATTACH_TYPE_EXP: {
      const angle = seconds / attach.periodS; // FlyingInstance.cpp:258
      return attach.amplitudeMm * Math.exp(-angle) * angle; // FlyingInstance.cpp:260-262
    }
    default:
      return 0; // FlyingInstance.cpp:213-218
  }
}

/** `UpdateAttachInstance` (`FlyingInstance.cpp:191-279`): wind the spin on by
 *  `dtS`, then put the attached effect `Roll` degrees round the head's axis at
 *  the reach its `FlyType` gives, in the frame the spin turns the flight's. */
function placeAttach(flight: Flight, dtS: number, nowMs: number): void {
  const script = flight.kind.script;
  const [spinX, spinY, spinZ] = script.spinDegS;
  // FlyingInstance.cpp:195-198
  const step = yawPitchRoll(radians(spinY) * dtS, radians(spinX) * dtS, radians(spinZ) * dtS);
  flight.spin = followedBy(flight.spin, step); // FlyingInstance.cpp:200
  const frame = followedBy(flight.spin, flight.turn); // FlyingInstance.cpp:201
  const reach = attachReachMm(script.attach, (nowMs - flight.startMs) / 1000);
  const roll = radians(script.attach.rollDeg);
  const push = rendered(rotated({ xMm: -Math.sin(roll) * reach, yMm: 0, zMm: -Math.cos(roll) * reach }, frame));
  flight.attachPrevious = flight.attachAt;
  flight.attachAt = { xMm: flight.at.xMm + push.xMm, yMm: flight.at.yMm + push.yMm, zMm: flight.at.zMm + push.zMm };
}

/** `AdjustDirectionForHoming`'s turn (`FlyingInstance.cpp:144-189`), in render
 *  space: the arc from the unit `from` onto the unit `to`, or null when the two
 *  already agree. Past a `HomingMaxAngle` of 180 the arc is whole; otherwise its
 *  half angle is clamped to `maxDeg`, so one step turns up to twice that. */
export function homingTurn(from: Point, to: Point, maxDeg: number): Quaternion | null {
  const miss = subtract(from, to);
  if (dot(miss, miss) < 0.001) return null; // FlyingInstance.cpp:152-153
  const q = arcBetween(from, to); // FlyingInstance.cpp:155
  if (maxDeg > 180) return q; // FlyingInstance.cpp:157-163
  const c = Math.cos(radians(maxDeg)); // FlyingInstance.cpp:165
  const s = Math.sin(radians(maxDeg)); // FlyingInstance.cpp:166
  if (q[3] <= -1 + 0.0001) return [0, 0, s, c]; // FlyingInstance.cpp:168-174
  if (q[3] <= c && q[3] <= 1 - 0.0001) {
    const factor = s / Math.sqrt(1 - q[3] * q[3]); // FlyingInstance.cpp:177
    return [q[0] * factor, q[1] * factor, q[2] * factor, c]; // FlyingInstance.cpp:178-181
  }
  return q;
}

/** A unit direction turned toward `to` by `homingTurn`, in the wire's space. */
export function turnToward(from: Point, to: Point, maxDeg: number): Point {
  const q = homingTurn(rendered(from), rendered(to), maxDeg);
  if (q === null) return from;
  return normalise(rendered(rotated(rendered(from), q))) ?? to;
}

export class SkillProjectiles {
  private readonly live = new Map<string, Flight>();
  private readonly finished: Landing[] = [];
  private lastMs = 0;

  /** `random` is the `[0, 1)` draw a spreading script's cone takes. */
  constructor(private readonly random: () => number = Math.random) {}

  launch(key: string, vnum: number, from: Point, to: Point, nowMs: number): Flight | null {
    const kind = skillFlyFor(vnum);
    return kind === null ? null : this.fly(key, vnum, kind, from, to, nowMs);
  }

  /** Launch what the server named by `FLY_*` type rather than by skill vnum --
   *  `CreateIndexedFly` (`PythonNetworkStreamPhaseGame.cpp:2654`). */
  launchIndexed(key: string, fly: number, from: Point, to: Point, nowMs: number): Flight | null {
    const kind = indexedFlyFor(fly);
    return kind === null ? null : this.fly(key, 0, kind, from, to, nowMs);
  }

  private fly(
    key: string,
    vnum: number,
    kind: FlightKind,
    from: Point,
    to: Point,
    nowMs: number,
  ): Flight | null {
    if (this.live.has(key)) return null;
    const aim = kind.script.maintainParallel === true ? { ...to, zMm: to.zMm + unit(50) } : to;
    const aimed = normalise(subtract(aim, from));
    if (aimed === null) return null;
    if (length(subtract(to, from)) > kind.script.rangeMm) return null;
    const turn = launchTurn(kind.script.cone, rendered(aimed), this.random);
    const direction = rendered(rotated(FORWARD, turn)); // FlyingInstance.cpp:130
    const flight: Flight = {
      key,
      vnum,
      kind,
      from,
      to,
      startMs: nowMs,
      at: from,
      previous: from,
      direction,
      speedMmS: kind.script.speedMmS,
      remainingMm: kind.script.rangeMm,
      piercesLeft: kind.script.pierceCount,
      turn,
      spin: IDENTITY,
      attachAt: from,
      attachPrevious: from,
      trail: [],
    };
    placeAttach(flight, 0, nowMs); // FlyingInstance.cpp:593
    flight.attachPrevious = flight.attachAt;
    this.live.set(key, flight);
    this.lastMs = nowMs;
    return flight;
  }

  advance(nowMs: number, targetAt?: (flight: Flight) => Point | null): void {
    const dt = (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (!(dt > 0)) return;
    for (const flight of [...this.live.values()]) {
      const script = flight.kind.script;
      const target = (targetAt?.(flight) ?? null) ?? flight.to;

      // 1. Homing, once `HomingStartTime` has passed.
      if (script.homing && (nowMs - flight.startMs) / 1000 >= script.homingStartS) {
        const wanted = normalise(subtract(target, flight.at));
        // `vVel` is the velocity's own direction, which is backwards while a
        // negative `InitialVelocity` has not yet been overcome.
        const heading = flight.speedMmS < 0 ? scaled(flight.direction, -1) : flight.direction;
        const q = wanted === null ? null : homingTurn(rendered(heading), rendered(wanted), script.homingMaxAngleDeg);
        if (q !== null) {
          // FlyingInstance.cpp:159-161,186-187
          flight.direction = normalise(rendered(rotated(rendered(flight.direction), q))) ?? flight.direction;
          // FlyingInstance.cpp:161,188
          flight.turn = script.homingMaxAngleDeg > 180 ? followedBy(q, flight.turn) : followedBy(flight.turn, q);
        }
      }

      // 2. Integrate. Acceleration is along the direction of travel and gravity
      //    is on world z, so the speed and the fall are separate terms -- and
      //    with `Gravity 0` throughout, the second one never fires.
      flight.speedMmS += script.accelMmS2 * dt;
      const step = {
        xMm: flight.direction.xMm * flight.speedMmS * dt,
        yMm: flight.direction.yMm * flight.speedMmS * dt,
        zMm: flight.direction.zMm * flight.speedMmS * dt + script.gravityMmS2 * dt * dt,
      };
      flight.previous = flight.at;
      flight.at = {
        xMm: flight.at.xMm + step.xMm,
        yMm: flight.at.yMm + step.yMm,
        zMm: flight.at.zMm + step.zMm,
      };
      placeAttach(flight, dt, nowMs); // FlyingInstance.cpp:368
      // `UpdateAttachInstance` feeds the tail the attached effect's new place
      // every update (`FlyingInstance.cpp:276-277`).
      if (script.tail !== null) traceSample(flight.trail, flight.attachAt, nowMs, script.tail.lengthS);

      // 3. Range is spent on distance travelled, and running out kills it where
      //    it is with no bomb.
      flight.remainingMm -= length(step);
      if (flight.remainingMm < 0) {
        this.live.delete(flight.key);
        this.finished.push({
          flight,
          ending: "out-of-range",
          at: flight.at,
          bombEffect: null,
        });
        continue;
      }

      // 4. Arrival, measured against the segment swept this step.
      if (segmentDistanceMm(flight.previous, flight.at, target) < script.bombRangeMm) {
        if (flight.piercesLeft > 0) {
          flight.piercesLeft -= 1;
          continue;
        }
        this.live.delete(flight.key);
        this.finished.push({
          flight,
          ending: "arrived",
          at: target,
          bombEffect: script.bombEffect,
        });
      }
    }
  }

  /** Everything still in the air, oldest launch first. */
  inFlight(): readonly Flight[] {
    return [...this.live.values()].sort((a, b) => a.startMs - b.startMs);
  }

  collect(): readonly Landing[] {
    const landed = this.finished.slice();
    this.finished.length = 0;
    return landed;
  }

  /** A new world: nothing that was in the air over the old one is over this one.
   *  The same call `EffectRenderer.clear` exists for. */
  clear(): void {
    this.live.clear();
    this.finished.length = 0;
  }

  get count(): number {
    return this.live.size;
  }
}

export function writeFlights(
  out: Float32Array,
  at: number,
  flights: readonly Flight[],
  cell: ArrayLike<number>,
  nowMs?: number,
): number {
  let written = 0;
  for (const flight of flights) {
    if ((at + written + 1) * INSTANCE_FLOATS > out.length) break;
    if (writeArrow(out, at + written, flight.attachAt, flight.attachPrevious, cell)) written += 1;
    const tail = flight.kind.script.tail;
    if (tail === null) continue;
    // `CFlyTrace::Render` (`FlyTrace.cpp:119-290`): each pair of samples is
    // one quad, its width at each end shrunk by how far that sample is
    // through `TailLength` unless the tail is `TailShapeRect`.
    const now = nowMs ?? flight.trail[0]?.atMs ?? flight.startMs;
    for (let i = 0; i + 1 < flight.trail.length; i += 1) {
      if ((at + written + 1) * INSTANCE_FLOATS > out.length) return written;
      const newer = flight.trail[i]!;
      const older = flight.trail[i + 1]!;
      const newerRate = tail.rect ? 1 : 1 - (now - newer.atMs) / 1000 / tail.lengthS;
      const olderRate = tail.rect ? 1 : 1 - (now - older.atMs) / 1000 / tail.lengthS;
      if (writeTrail(out, at + written, newer, older, tail.halfWidthMm * newerRate, tail.halfWidthMm * olderRate, tail.colour)) {
        written += 1;
      }
    }
  }
  return written;
}
